/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { useSelector } from 'react-redux';

import { CartActions, SecurityHelper } from 'sarsaparilla';

import { useTypedDispatch } from '../../../redux';

import CartLimitExceededModal from './CartLimitExceededModal';
import CartWarningModal from './CartWarningModal';
import type { ReduxStore } from '../Navbar.types';
import { CartIndicator } from './CartIndicator';

export function CartIndicatorContainer() {
    const [isFinalWarning, setIsFinalWarning] = React.useState(false);
    const dispatch = useTypedDispatch();

    const cart = useSelector((state: ReduxStore) => state.cart);
    const isLoggedIn = useSelector((state: ReduxStore) => Boolean(state.auth?.account));
    const isCSRExternal = SecurityHelper.isExternalCSRLoggedIn();

    // Fetch the cart if the user is logged in
    React.useEffect(() => {
        if (isLoggedIn) {
            dispatch(CartActions.fetchCart(true));
        }
    }, [dispatch, isLoggedIn]);

    // Don't show the cart indicator if the user is not logged in
    if (!isLoggedIn) {
        return null;
    }

    return (
        <>
            <CartIndicator
                expiration={cart?.expiration}
                showTimer={cart?.showTimer}
                reservationCount={cart?.reservation_count}
            />

            <CartLimitExceededModal />

            {cart.showTimer || isFinalWarning ? (
                <CartWarningModal
                    sessionWarning={isCSRExternal ? false : cart.showSessionWarning}
                    expiration={cart.expiration}
                    expiresIn={cart.expiresIn}
                    isExtendingTime={cart.isExtendingTime}
                    extendTimer={() => {
                        dispatch(CartActions.extendTimer());
                    }}
                    fetchCart={(isLightweight) => {
                        dispatch(CartActions.fetchCart(isLightweight));
                    }}
                    onFinalCartWarningModal={() => {
                        setIsFinalWarning(true);
                    }}
                />
            ) : null}
        </>
    );
}
